import { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiErrorHandling';
import useSnackbar from './useSnackbar';

const createBrandPagination = () => ({
    total: 0,
    page: 1,
    size: 10,
    pages: 0,
    has_next: false,
    has_prev: false
});

const useBrands = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState(createBrandPagination());
    const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

    const fetchBrands = async (page = 1, size = 10, filters = {}) => {
        setLoading(true);
        setError(null);
        
        try {
            const params = {
                page: page,
                size: size
            };

            // 필터 파라미터 추가
            if (filters.keyword) {
                params.keyword = filters.keyword;
            }
            if (filters.source_site) {
                params.source_site = filters.source_site;
            }
            
            const result = await apiClient.get('/brands/', { params });
            console.log('Brands API 응답:', result.data);
            
            // 응답 데이터 확인
            if (result.data && result.data.items) {
                setBrands(result.data.items);
                setPagination({
                    ...createBrandPagination(),
                    total: result.data.total || 0,
                    page: result.data.page || 1,
                    size: result.data.size || 10,
                    pages: result.data.pages || 0,
                    has_next: result.data.has_next || false,
                    has_prev: result.data.has_prev || false
                });
            } else {
                console.error('예상하지 못한 응답 구조:', result.data);
                setError('브랜드 데이터를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('브랜드 불러오기 에러:', error);
            setError(error.response?.data?.message || '브랜드를 불러오는데 실패했습니다.');
            showSnackbar(error.response?.data?.message || '브랜드를 불러오는데 실패했습니다.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const createBrand = async (brandData) => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiClient.post('/brands/', brandData);
            console.log('Brand 생성 응답:', result.data);
            
            showSnackbar('브랜드가 성공적으로 생성되었습니다.', 'success');
            
            // 현재 페이지 다시 불러오기 (필터 유지)
            await fetchBrands(pagination.page, pagination.size, {});
            
            return { success: true };
        } catch (error) {
            console.error('브랜드 생성 에러:', error);
            const errorMessage = error.response?.data?.message || '브랜드 생성에 실패했습니다.';
            setError(errorMessage);
            showSnackbar(errorMessage, 'error');
            return { success: false, error };
        } finally {
            setLoading(false);
        }
    };

    const updateBrand = async (brandId, brandData) => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiClient.put(`/brands/${brandId}`, brandData);
            console.log('Brand 업데이트 응답:', result.data);
            
            showSnackbar('브랜드가 성공적으로 수정되었습니다.', 'success');
            
            return { success: true };
        } catch (error) {
            console.error('브랜드 수정 에러:', error);
            const errorMessage = error.response?.data?.message || '브랜드 수정에 실패했습니다.';
            setError(errorMessage);
            showSnackbar(errorMessage, 'error');
            return { success: false, error };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrands(1, 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        brands,
        loading,
        error,
        pagination,
        fetchBrands,
        createBrand,
        updateBrand,
        snackbar,
        showSnackbar,
        closeSnackbar
    };
};

export default useBrands;

